import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Floating "SAVE ALL NOTES (N)" bar.
 *
 * Listens for `admin-note-dirty` window events from OrderCards. When any are
 * dirty, renders a sticky bar at the bottom-right. Click to broadcast a single
 * `admin-notes-save-all` event — every dirty card responds in parallel.
 */
export default function AdminNotesBulkBar() {
  // Use a ref-backed Set + a counter state so re-renders happen but the Set
  // itself isn't invalidated by closures.
  const dirtyRef = useRef(new Set());
  const [dirtyCount, setDirtyCount] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onDirty = (e) => {
      const { orderId, dirty } = e.detail || {};
      if (!orderId) return;
      const set = dirtyRef.current;
      if (dirty) set.add(orderId);
      else set.delete(orderId);
      setDirtyCount(set.size);
    };
    window.addEventListener('admin-note-dirty', onDirty);
    return () => window.removeEventListener('admin-note-dirty', onDirty);
  }, []);

  const handleSaveAll = async () => {
    if (dirtyCount === 0 || saving) return;
    setSaving(true);
    const count = dirtyCount;
    try {
      // Each OrderCard listens for this event and PUTs its own notes in parallel.
      window.dispatchEvent(new CustomEvent('admin-notes-save-all'));
      // Give network requests a beat to flush.
      await new Promise((r) => setTimeout(r, 600));
      toast.success(`SAVED ${count} NOTE${count === 1 ? '' : 'S'}`);
      dirtyRef.current.clear();
      setDirtyCount(0);
    } catch (e) {
      toast.error('SOME NOTES FAILED TO SAVE');
    } finally {
      setSaving(false);
    }
  };

  if (dirtyCount === 0) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 bg-black border border-orange-500 shadow-2xl px-4 py-3 flex items-center gap-3"
      data-testid="admin-notes-bulk-bar"
    >
      <span className="font-mono text-[11px] text-orange-400">
        {dirtyCount} UNSAVED NOTE{dirtyCount === 1 ? '' : 'S'}
      </span>
      <button
        onClick={handleSaveAll}
        disabled={saving}
        className="px-4 py-2 bg-orange-500 text-black font-mono text-xs font-bold hover:bg-orange-400 disabled:opacity-50"
        data-testid="admin-notes-save-all-btn"
      >
        {saving ? 'SAVING…' : 'SAVE ALL NOTES'}
      </button>
    </div>
  );
}
